import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import SearchIcon from '@/images/Search.svg';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import styled from '@emotion/styled';
import ClearIcon from '@mui/icons-material/Clear';
import { InputAdornment, TextField } from '@mui/material';
import debounce from 'lodash/debounce';
import { useCallback, useRef } from 'react';

const StyledTextField = styled(TextField)`
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 8px;
    width: 100%;
    max-width: 344px;

    & .MuiInputBase-input {
        &::placeholder {
            color: ${({ theme }) => theme.colors.black[700]};
            opacity: 1;
        }
    }

    & .MuiOutlinedInput-root {
        & fieldset {
            border: none !important;
        }
    }

    input {
        font-size: 16px;
        line-height: 20px;
        color: ${({ theme }) => theme.colors.black[900]};
        font-weight: 400;
        caret-color: ${theme.colors.primary[900]};
    }

    box-shadow: 0px 1.32px 8px 0px rgba(0, 0, 0, 0.025);
`;

const StyledInputAdornment = styled(InputAdornment)`
    color: rgba(0, 0, 0, 0.3);
`;

interface Props {
    search: string;
    setSearch: (search: string) => void;
    location: 'modal' | 'side-panel';
}

export function TemplateSearch({ search, setSearch, location }: Props) {
    const mixpanel = useAnalytics();
    const reportedValue = useRef(search);

    const reportTemplateSearch = useCallback((search: string) => {
        if (reportedValue.current === search) {
            return;
        }

        reportedValue.current = search;
        mixpanel?.track('text-search', {
            searchType: 'templates',
            componentType: location,
            searchQuery: search,
        });
    }, []);

    const debouncedReportTemplateSearch = useCallback(debounce(reportTemplateSearch, 2000), [reportTemplateSearch]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        debouncedReportTemplateSearch(e.target.value);
    };

    const handleClearSearch = () => {
        setSearch('');
    };

    return (
        <StyledTextField
            placeholder="Search templates"
            onChange={handleSearchChange}
            value={search}
            size="small"
            inputProps={{
                'data-testid': 'template-search-field',
                maxLength: 100,
            }}
            InputProps={{
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                        reportTemplateSearch(search);
                    }
                },
                onBlur: () => reportTemplateSearch(search),
                startAdornment: (
                    <StyledInputAdornment position="start">
                        <MaskedIcon src={SearchIcon} size="22px" color={theme.colors.black[600]} />
                    </StyledInputAdornment>
                ),
                endAdornment: search ? (
                    <StyledInputAdornment position="end">
                        <ClearIcon cursor="pointer" onClick={handleClearSearch} />
                    </StyledInputAdornment>
                ) : null,
            }}
        />
    );
}
